// Publishes a viewer's downloadable file types into the shared download registry
// (HomepageContext), which the single `UniversalDownloadMenu` renders.
//
// The active viewer calls this with the `DownloadFormat[]` for the current
// model; when `active` is false (an inactive tab) or `formats` is empty, it
// clears its registration. Clears on unmount too, so switching models/tabs never
// leaves a stale download menu behind.
//
// IMPORTANT: memoize `formats` (useMemo) in the caller so this doesn't
// re-register every render — the effect re-runs whenever the array identity
// changes.

import { useEffect, useId } from 'react';
import { useHomepageOptional } from '@/contexts/homepage-context';
import type { DownloadFormat } from './DownloadTypes';

export function useRegisterDownload(
  formats: DownloadFormat[] | null,
  active = true,
): void {
  const registerDownload = useHomepageOptional()?.registerDownload;
  // Stable per-caller identity so clears only affect THIS registrant's slot.
  const ownerId = useId();

  useEffect(() => {
    if (!registerDownload) return;
    if (active && formats && formats.length > 0) {
      registerDownload(ownerId, formats);
      return () => registerDownload(ownerId, null);
    }
    registerDownload(ownerId, null);
  }, [registerDownload, ownerId, active, formats]);
}
